import {Component, OnInit, inject, DestroyRef} from '@angular/core';
import {BreadcrumbItem} from 'src/app/models/breadcrumb/breadcrumb-item';
import {translate} from '@jsverse/transloco';
import {Solution} from 'src/app/models/service/solution';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {finalize, map} from 'rxjs/operators';
import {DynamicFormModel} from 'src/app/models/service/dynamic-form-model';
import {SolutionsService} from 'src/app/services/api/solutions.service';
import {FormUtilService} from 'src/app/services/util/form-util.service';
import {InstallationSpecificationService} from 'src/app/services/util/installation-specification.service';
import {BlueprintsService} from 'src/app/services/api/blueprints.service';
import {Observable, Subscription} from 'rxjs';
import {Blueprint, BlueprintTypes} from 'src/app/models/blueprints/blueprint';
import {ActivatedRoute, Router} from '@angular/router';
import {RouterSegments} from 'src/app/models/router-segments';
import {AuthService} from 'src/app/services/auth/auth.service';
import {ScrollService} from 'src/app/services/util/scroll.service';
import {MatDialog} from '@angular/material/dialog';
import {ErrorDialogComponent} from 'src/app/components/error-dialog/error-dialog.component';
import {InstancesService} from 'src/app/services/api/instances.service';
import {Instance} from 'src/app/models/instance/instance';
import {TenantsService} from 'src/app/services/api/tenants.service';
import {TabId} from 'src/app/models/blueprints/summary-tabs';
import { Title } from '@angular/platform-browser';
import { ToastrService } from 'src/app/services/util/toastr.service';
import { Subject } from 'rxjs';
import { DataZone } from 'src/app/models/data-zone/data-zone';
import { DeploymentClass } from 'src/app/models/instance-class';
import { Deployment } from 'src/app/models/deploy/deployment';
import { DeploymentsService } from 'src/app/services/api/deployments.service';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { PermissionKeys } from 'src/app/models/principal/permission-keys';
import { Permissions } from 'src/app/models/principal/permission';
import { PermissionsService } from 'src/app/services/util/permissions.service';

const UPDATED_REQUIRED_PERMISSIONS = [
  {
    key: PermissionKeys.BLUEPRINT_CREATE,
    action: Permissions.BLUEPRINT_CREATE
  },
];

@Component({
    selector: 'app-service-definition',
    templateUrl: './service-definition.component.html',
    styleUrls: ['./service-definition.component.scss'],
    standalone: false
})
export class ServiceDefinitionComponent implements OnInit {

  service: Solution;
  breadcrumbItems: BreadcrumbItem[];
  dataLoaded = false;

  blueprintForm: FormGroup;
  installationSpecificationForms: any;
  showQuestionnaireSection = true;
  // -1 is blueprint form
  currentStep = -1;
  stepsArray = [];
  highestStep = -1;
  isLoading: boolean;
  tenant: string;
  instances: Instance[];
  blueprint: Blueprint[];
  blueprintId: Blueprint;
  serviceLoader = false;
  tenantRegion: any;
  blueprintLatestVersion: any;
  getSelectedCapabilities = JSON.parse(localStorage.getItem('selectedCapabilities'));
  TabId = TabId;
  selectedTab = TabId.SETUP;
  descriptionArray: any;
  errorMessage: any;
  filterSubscription: Subscription;
  sandbox: any;
  demoInstance: Instance;
  sysGenDataEnable: any;
  sysGenData = false;
  solutionVersion: any;
  sortedSolutionVersionId: any;
  instanceSolutionVersion: string;
  permissionsMap: { [key: string]: boolean };
  additionalCapabilties: any;
  includedCapabilities: any;
  optionalCapabilities: any;
  private destroyRef = inject(DestroyRef);

  readonly INSTANCE_FILTERED_STATES = [DeployStatus.DEPLOYED, DeployStatus.FAILED, DeployStatus.DEPLOYING,
    DeployStatus.CONFIGURING, DeployStatus.CONFIGURATION_FAILED, DeployStatus.CONFIGURED, DeployStatus.TESTED, DeployStatus.UPDATED,
    DeployStatus.UPDATE_FAILED, DeployStatus.TEST_FAILED, DeployStatus.FAILOVER_APPLYING, DeployStatus.FAILBACK_APPLYING,
    DeployStatus.PRE_FAILBACK_APPLYING, DeployStatus.FAILOVER_FAILED, DeployStatus.FAILBACK_FAILED, DeployStatus.PRE_FAILBACK_FAILED];

  constructor(
    private matDialog: MatDialog, private activatedRoute: ActivatedRoute,private formBuilder: FormBuilder,
    private solutionsService: SolutionsService, private installationSpecificationService: InstallationSpecificationService,
    private blueprintsService: BlueprintsService, private router: Router,  private authService: AuthService,
    private instancesService: InstancesService, private scrollService: ScrollService, private tenantService: TenantsService,
    private readonly titleService: Title, private deploymentsService: DeploymentsService, private toastrService: ToastrService,
    private permissionsService: PermissionsService) {
  }

  ngOnInit(): void {
    this.tenant = this.authService.principal.provider;
    this.permissionsMap = this.permissionsService.resolveAPIPermissions(UPDATED_REQUIRED_PERMISSIONS);
    if (!this.permissionsMap[PermissionKeys.BLUEPRINT_CREATE]) {
      this.router.navigate(['/', this.tenant, RouterSegments.UNAUTHORIZED], {skipLocationChange: true});
    }
    this.additionalCapabilties = [];
    this.includedCapabilities = this.getSelectedCapabilities?.filter(item => item.required === 'mandatory');
    this.optionalCapabilities = this.getSelectedCapabilities?.filter(item => item.required === 'optional');
    this.getSelectedCapabilities?.forEach(item => {
      if(item.nestedObj) {
      const additional = item?.nestedObj?.filter(data => data.selected === true);
      this.additionalCapabilties = [...this.additionalCapabilties, ...additional];
      }
    });
    this.additionalCapabilties.forEach(item => {
      if (!this.getSelectedCapabilities.find(element => (element.name === item.name) && (item.selected === true))) {
        this.getSelectedCapabilities = [...this.getSelectedCapabilities, item];
      }
    });
    this.loadData();
    this.sandbox = history.state.sandbox;
    this.solutionVersion = history.state.solutionVersion;
  }

  loadData(): void {
    const serviceId = this.activatedRoute.snapshot.params.serviceId;
    this.solutionsService.getSolutionDetailsById(serviceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(serviceDetails => {
        this.service = serviceDetails;
        this.titleService.setTitle(this.service?.name);
        this.createBlueprintForm();
        this.configureBreadcrumb();
        this.createInstallationDocumentForms(this.sysGenData);
        this.getTenantDetails();
        this.solutionsService.getSolutionVersions(this.service?.id).pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(data => {
          this.sortedSolutionVersionId =
          (data.sort((a,b) => a?.version && b?.version ? b?.version?.localeCompare(a?.version) : !b?.version ? -1 : 1));
        });
      });
    this.blueprintId = this.activatedRoute.snapshot.params.blueprintId;
    if (this.blueprintId) {
      this.getTenantDetails();
      this.loadBlueprint();
      this.getBlueprintsIdLatestVersion(this.blueprintId);
    }
  }

  getTenantDetails(): void {
    this.tenantService.getTenantDetails()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(tentant => {
        const tenantRegion = tentant.region;
        // const tenantRegion = tentant.temenosRegion;
        this.tenantRegion = [translate('TEMENOS.NAME'), tenantRegion].filter(Boolean).join(' ');
        this.blueprintForm?.controls.region.setValue(this.tenantRegion);
      });
  }

  sysGen(event?): void {
    this.sysGenData = event.checked;
    if(this.sysGenData === true) {
      this.sysGenDataEnable = true;
      this.createInstallationDocumentForms(this.sysGenData);
    } else {
      this.createInstallationDocumentForms(this.sysGenData);
      this.sysGenDataEnable = false;
    }
  }

  setup(): void {
    if (this.currentStep !== -1 && !this.blueprintId) {
      // validation has already passed
      this.setupBlueprint();
      return;
    } else if (this.currentStep !== -1 && this.blueprintId) {
      this.updateBlueprints(this.blueprintId);
      return;
    }
    this.isBlueprintNameInvalid()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(invalid => {
        if (invalid) {
          this.setErrorToNameControl();
        } else {
          this.setupBlueprint();
        }
      });
  }

  loadBlueprint(): void {
    this.blueprintsService.getBlueprints()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(blueprints => {
        this.blueprint = blueprints.filter(blueprint => blueprint.id === this.activatedRoute.snapshot.params.blueprintId);
        if(this.blueprint[0].onboardingProcess === 'sysgen') {
          this.sysGenData = true;
          this.createInstallationDocumentForms(this.sysGenData);
        } else {
          this.sysGenData = false;
          this.createInstallationDocumentForms(this.sysGenData);
        }
        setTimeout(() => {
          this.blueprintForm.get('name').setValue(this.blueprint[0].name);
        }, 1000);

      });

  }

  goBack(): void {
    const blueprintId = this.activatedRoute.snapshot.params.blueprintId;
    if(blueprintId) {
      this.router.navigate(['/', this.tenant, RouterSegments.SERVICES, this.service?.id, RouterSegments.EXTEND,
        this.activatedRoute.snapshot.params.blueprintId ]);
    } else {
      this.router.navigate(['/', this.tenant, RouterSegments.SERVICES, this.service.id]);

    }
  }

  goToStep(step: number): void {
    if (this.blueprintForm) {
      this.blueprintForm.markAsTouched();
    }

    if (step === -2) {
      step = -1;
    }
    if (step !== 0 && this.getSelectedCapabilities?.length > 0) {
      this.updateFields();
    }
    this.scrollService.scrollTo(0);
    this.showQuestionnaireSection = true;
    if (step === this.currentStep) {
      return;
    }

    if (step > this.highestStep + 1) {
      return;
    }
    const goingBackward = step < this.currentStep;
    if (this.currentStep === -1 && !this.blueprintId) {
      if (goingBackward || FormUtilService.validateForm(this.blueprintForm)) {
        this.isBlueprintNameInvalid()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(invalid => {
            if (invalid) {
              this.setErrorToNameControl();
            } else {
              this.currentStep = step;
              this.highestStep = Math.max(this.highestStep, this.currentStep);
              this.showQuestionnaireSection = step < this.installationSpecificationForms.length;
            }
          });
      }
    }
    else if (!goingBackward && this.blueprintId) {
      this.blueprintForm.get('name').clearValidators();
      this.blueprintForm.updateValueAndValidity();
      this.blueprintForm.get('region').clearValidators();
      this.blueprintForm.updateValueAndValidity();
      this.currentStep = step;
      this.showQuestionnaireSection = step < this.installationSpecificationForms.length;
    }
    else if (goingBackward || FormUtilService.validateForm(this.installationSpecificationForms[this.currentStep].formGroup)) {
      this.currentStep = step;
      this.showQuestionnaireSection = step < this.installationSpecificationForms.length;
    }
    this.highestStep = Math.max(this.highestStep, this.currentStep);
    this.serviceLoader = false;
    if (this.installationSpecificationForms) {
      this.installationSpecificationForms.forEach(element => {
        if (!element?.formGroup.touched && element?.formGroup.status === 'VALID') {
         element?.formGroup?.markAsTouched();
        }
      });
    }
  }

  updateFields(): void {
    if (this.installationSpecificationForms) {
      this.installationSpecificationForms?.forEach((primaryForm, primaryIndex) => {
        this.installationSpecificationForms?.forEach((secondaryForm, secondaryIndex) => {
          if (primaryIndex !== secondaryIndex) {
            Object?.keys(primaryForm?.property?.label)?.forEach(primaryLabel => {
              Object?.keys(secondaryForm?.property?.label)?.forEach(secondaryLabel => {
                if (primaryForm.property.label[primaryLabel] === (secondaryForm.property.label[secondaryLabel])) {
                  if (primaryForm.formGroup.controls[primaryForm?.property?.name]?.get(primaryLabel)?.value) {
                    if (secondaryForm?.formGroup?.controls[secondaryForm?.property?.name]?.get(secondaryLabel)) {
                      secondaryForm?.formGroup?.controls[secondaryForm?.property?.name]?.get(secondaryLabel).setValue(
                        primaryForm?.formGroup?.controls[primaryForm?.property?.name]?.get(primaryLabel)?.value);
                    }
                  }
                }
              });
            });
          }
        });
      });
    }
  }

  getBlueprintsIdLatestVersion(blueprintId): void {
    this.blueprintsService.getBlueprintsIdLatestVersion(blueprintId)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(blueprintLatestVersion => {
      this.blueprintLatestVersion = blueprintLatestVersion;
    }, (error) => {
      this.toastrService.clear();
          setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
    });
  }

  private configureBreadcrumb(): void {
    this.breadcrumbItems = [];
    this.breadcrumbItems.push({label: translate('HOME.HOME'), url:[]});
    this.breadcrumbItems.push({label: translate('COMPOSE.COMPOSE_SERVICE'), url:['compose']});
    // this.breadcrumbItems.push({label: translate('CATALOGUE.CATALOGUE'), url: ['catalogue']});
    // this.breadcrumbItems.push({label: this.service.name, url:['services',this.service.id]});
  }

  private createBlueprint(blueprint: Blueprint): void {
    // if(this.sysGenData === true) {
    //   blueprint.onboardingProcess = 'sysgen';
    // } else {
    //   blueprint.onboardingProcess = 'migration';
    // }

    blueprint.onboardingProcess = this.solutionVersion?.onboardingProcess ? this.solutionVersion?.onboardingProcess : 'null';
    this.filterSubscription =  this.blueprintsService.createBlueprint(blueprint)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((createdBlueprint) => {
          if (createdBlueprint) {
            this.createBlueprintVersion(createdBlueprint.id, this.sortedSolutionVersionId[0]?.id);
          }
        },
        (error) => {
          this.matDialog.open(ErrorDialogComponent, {
            minHeight: '342px',
            width: '566px',
            panelClass: 'flex-dialog',
            data: {
              title:  translate('NOTIFICATIONS.BLUEPRINT_CREATION_FAILED'),
              description: error,
            }
          });
        }
      );
  }

  private onCreateSandbox(blueprintValue): void {
    const capabilities = this.getSelectedCapabilities;
    let deployment;
    const getSelectedCapabilities = capabilities;
    const installDocRaw = this.getFilledInstallationDocument();
    const blueprint = {
      solutionId: this.service.id,
      name: blueprintValue.name,
      demo: 'true',
      onboardingProcess: this.solutionVersion?.onboardingProcess ? this.solutionVersion?.onboardingProcess : 'null',
      region: blueprintValue.region
    } as any;

    if (capabilities && capabilities.length > 0) {
      capabilities.filter((x) => {
        if (x.selected) {
          getSelectedCapabilities.push(x.name);
        }
      });
    }
    this.demoInstance = {
      status: DeployStatus.DEPLOYING
    } as Instance;

    this.blueprintsService.createBlueprint(blueprint).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (responseBlueprint) => {
         // eslint-disable-next-line max-len
         this.blueprintsService.createBlueprintVersion(responseBlueprint.id, this.sortedSolutionVersionId[0]?.id, getSelectedCapabilities, installDocRaw)
          .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          (blueprintVersion: any) => {
            this.router.navigate(['/', this.authService.principal.provider, RouterSegments.SERVICES, this.service.id,
              RouterSegments.BLUEPRINTS, responseBlueprint.id]);
            deployment = {
              blueprintId: responseBlueprint.id,
              blueprintVersionId: blueprintVersion.id,
              region: this.service.regionalAvailability[0],
              dataZone: DataZone.DEVELOPMENT,
              deploymentClass: DeploymentClass.D,
              name: translate('BLUEPRINT_SUMMARY.DEMO_INSTANCE_SANDBOX'),
              capabilities: getSelectedCapabilities,
              type: 'sandbox'
            } as Deployment;
            this.deploymentsService.createAndDeploy(deployment)
              .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => { },
              (error) => {
                this.deployInstanceError(error);
                // Remove demoInstance if deployment fail to avoid disabling the button.
                delete this.demoInstance;
              });
          }, (error) => {
            this.deployInstanceError(error);
            // Remove demoInstance if deployment fail to avoid disabling the button.
            delete this.demoInstance;
          });
      }, (error) => {
        this.deployInstanceError(error);
        // Remove demoInstance if deployment fail to avoid disabling the button.
        delete this.demoInstance;
      });
  }

  private deployInstanceError(error): void {
    // this.sandboxLoader = false;
    this.matDialog.open(ErrorDialogComponent, {
      minHeight: '342px',
      width: '566px',
      panelClass: 'flex-dialog',
      data: {
        title: translate('NOTIFICATIONS.SANDBOX_FAILED'),
        description: error,
      }
    });
  }

  private updateBlueprints(blueprint: any): void {
     if (blueprint) {
      this.serviceLoader = true;
      const serviceId = this.activatedRoute.snapshot.params.solutionId;
      const blueprintId = this.activatedRoute.snapshot.params.blueprintId;
      this.solutionsService.getSolutionVersions(serviceId).pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(data => {
            this.sortedSolutionVersionId =
            (data.sort((a,b) => a?.version && b?.version ? b?.version?.localeCompare(a?.version) : !b?.version ? -1 : 1));
          });
      this.instancesService.getInstancesForBlueprint(blueprintId)
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (data: any) => {
        if(data?.data?.length > 0) {
        const filteredInstances = data.data.filter(item => this.INSTANCE_FILTERED_STATES.includes(item.status));

      this.instanceSolutionVersion = (filteredInstances?.find(instances => instances.deploymentClass === DeploymentClass.A)) ? (filteredInstances?.find(instances => instances.deploymentClass === DeploymentClass.A).solutionVersionId)
      : (filteredInstances?.find(instances => instances.deploymentClass === DeploymentClass.B)) ? (filteredInstances.filter(i => i.deploymentClass === DeploymentClass.B)[filteredInstances.filter(i => i.deploymentClass === DeploymentClass.B).length -1].solutionVersionId)
      : (filteredInstances?.find(instances => instances.deploymentClass === DeploymentClass.C)) ? (filteredInstances.filter(i => i.deploymentClass === DeploymentClass.C)[filteredInstances.filter(i => i.deploymentClass === DeploymentClass.C).length -1].solutionVersionId)
      : (filteredInstances?.find(instances => instances.deploymentClass === DeploymentClass.D)) ? (filteredInstances.filter(i => i.deploymentClass === DeploymentClass.D)[filteredInstances.filter(i => i.deploymentClass === DeploymentClass.D).length -1].solutionVersionId)  : this.sortedSolutionVersionId[0].id;
        }
      });
      this.createBlueprintVersion(blueprint, this.instanceSolutionVersion);

      this.router.navigate(['/', this.tenant, RouterSegments.SERVICES, this.service.id,
          RouterSegments.BLUEPRINTS, blueprint], { state: { data: BlueprintTypes.SERVICE_DEFINITION } });    }

  }

  private updateBlueprint(blueprint: Blueprint): void {
    this.blueprintsService.updateBlueprint(blueprint)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((createdBlueprint) => {
        if (createdBlueprint) {
          this.createBlueprintVersion(createdBlueprint.id);
          this.router.navigate(['/', this.tenant, RouterSegments.SERVICES, this.service.id,
            RouterSegments.BLUEPRINTS, createdBlueprint.id]);
        }
      },
      (error) => {
        this.matDialog.open(ErrorDialogComponent, {
          minHeight: '342px',
          width: '566px',
          panelClass: 'flex-dialog',
          data: {
            title:  translate('NOTIFICATIONS.UPDATE_SERVICE_DEFINITION_FAILED'),
            description: error,
          }
        });
      });
  }

  private createBlueprintVersion(id: string, solutionVersionId?): void {
    const capabilities = this.getSelectedCapabilities.map(item => item.name);
    const totalCapabilities = capabilities;
    const installDocRaw = this.getFilledInstallationDocument();
    this.installationSpecificationForms.forEach(item => {
      item?.property?.properties?.forEach(propertyItem => {
        const currentValue = installDocRaw[item.property.name][propertyItem.name];
        if(propertyItem['x-edit'] === 'append' && propertyItem.updatedValue) {
          propertyItem?.updatedValue?.split(',')?.map(item => item?.trim()).forEach(value => {
            if (!(currentValue?.split(',')?.map(item => item?.trim())?.includes(value))) {
              installDocRaw[item.property.name][propertyItem.name] = value + ', ' + installDocRaw[item.property.name][propertyItem.name];
            }
          });
        }
      });
    });
    this.blueprintsService.createBlueprintVersion(id, solutionVersionId, totalCapabilities, installDocRaw)
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe(() => {
      this.router.navigate(['/', this.tenant, RouterSegments.SERVICES,
      this.service.id, RouterSegments.BLUEPRINTS, id]);
    },
    (error) => {
      this.blueprintsService.deleteBlueprint(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.serviceLoader = false;
      },
      (errors) => {
        this.matDialog.open(ErrorDialogComponent, {
          minHeight: '342px',
          width: '566px',
          panelClass: 'flex-dialog',
          data: {
            title:  translate('NOTIFICATIONS.DELETE_SERVICE_DEFINITION_FAILED'),
            description: errors,
          }
        });
      });
      this.matDialog.open(ErrorDialogComponent, {
        minHeight: '342px',
        width: '566px',
        panelClass: 'flex-dialog',
        data: {
          title:  translate('NOTIFICATIONS.BLUEPRINT_CREATION_FAILED'),
          description: error,
        }
      });
    }
  );
  }

  private getFilledInstallationDocument(): void {
    const bluePrintFormIsValid = FormUtilService.validateForm(this.blueprintForm);
    const installationSpecificationFormsAreValid = this.installationSpecificationForms.reduce(
      (result, installationSpecificationForm) =>
        result && FormUtilService.validateForm(installationSpecificationForm.formGroup), true);
    if (bluePrintFormIsValid && installationSpecificationFormsAreValid) {
      const blueprint = this.blueprintForm.getRawValue();
      if (this.installationSpecificationForms.length > 0) {
        const installationDocument = {};
        this.installationSpecificationForms.forEach(installationSpecificationForm => {
          const installationSpecificationFormValue = this.installationSpecificationService.getFormValue(
            installationSpecificationForm);
          Object.assign(installationDocument, installationSpecificationFormValue);
        });
        blueprint.installationDocument = installationDocument;
      }
      return blueprint.installationDocument;
    }
  }

  private setupBlueprint(): void {
    this.serviceLoader = true;
    const blueprint = this.blueprintForm.getRawValue();
    if(this.sandbox) {
      this.onCreateSandbox(blueprint);
    } else {
    this.createBlueprint(blueprint);
    }
    localStorage.removeItem('selectedCapabilities');
  }

  private isBlueprintNameInvalid(): Observable<boolean> {
    this.isLoading = true;
    return this.blueprintsService.getBlueprints()
      .pipe(
        finalize(() => this.isLoading = false),
        map((blueprints) => {
          const currentBlueprintName = this.blueprintForm.get('name').value?.toLowerCase();
          return blueprints.some(blueprint => blueprint?.name?.toLowerCase() === currentBlueprintName);
        }));
  }

  /**
   * setErrors() happens before validation and errors are afterwards overwritten by the validators. Applies for controls in form groups.
   *
   * https://github.com/angular/angular/issues/38191
   */
  private setErrorToNameControl(): void {
    setTimeout(() => this.blueprintForm.get('name').setErrors({nameNotUnique: true}));
  }

  private createBlueprintForm(): any {
    this.blueprintForm = this.formBuilder.group({
      name: [undefined, [Validators.required,Validators.minLength(6), Validators.maxLength(16),
        Validators.pattern(/^([A-Za-z])([a-zA-Z0-9_])+( [a-zA-Z0-9_]+)*$/)]],
      demo: false,
      solutionId: [this.service.id],
      region: [undefined, Validators.required],
    });
  }

  private createInstallationDocumentForms(sysGenValue): void {
    this.installationSpecificationForms = [];
    const solutionVersion = (!this.instanceSolutionVersion || this.instanceSolutionVersion === undefined) ?
    this.service?.latestSolutionVersionId : this.instanceSolutionVersion;
    this.solutionsService.getSolutionVersions(this.service?.id)
    .pipe(
      map(allVersions => allVersions.find(version => version.id === solutionVersion)),
      finalize(() => this.dataLoaded = true),
      takeUntilDestroyed(this.destroyRef)
    )
    .subscribe(latestSolutionVersion => {
      if (latestSolutionVersion.installationSpecification || latestSolutionVersion.sandboxInstallationSpec) {
        if(history.state.sandbox) {
          this.installationSpecificationForms = this.installationSpecificationService.buildDynamicForms(
            atob(latestSolutionVersion.sandboxInstallationSpec)
          );
        } else {
          this.installationSpecificationForms = this.installationSpecificationService.buildDynamicForms(
            atob(latestSolutionVersion.installationSpecification)
          );
        //  if(!sysGenValue) {
        //    this.installationSpecificationForms?.forEach((element,index)=>{
        //      if(element.property.name === 'transactParameters') {
        //        this.installationSpecificationForms.splice(index,1);
        //      }
        //   });
        //  }
        }

        let installParameters = [];
        let uniqueInstallParams: any;
        const installParamItems = [];

        if (this.blueprintLatestVersion?.installDocRaw) {
          const installDocRaw = this.blueprintLatestVersion?.installDocRaw;
          this.getSelectedCapabilities.forEach(capability => {
            if (capability.mappingSpecification) {
              const mapping = this.installationSpecificationService.buildDynamicForms(
                atob(capability.mappingSpecification)
              );
              mapping?.forEach(map => {
                map?.property?.properties?.forEach((field: any) => {
                  const append = this.installationSpecificationForms?.find(item => item.property.name === map?.property.name)?.property?.properties.find(item => item.name === field.name)['x-edit'] === 'append';
                  if (installDocRaw[map?.property.name] && append) {
                    if (installDocRaw[map?.property.name][field.name] && JSON.stringify(installDocRaw[map?.property.name][field.name]) !== '{}' && append) {
                      const previousValues = installDocRaw[map?.property.name][field.name]?.split(',')?.map(item => item.trim());
                      field.default.split(',')?.map(item => item.trim()).forEach(item => {
                        if (!previousValues.includes(item)) {
                          installDocRaw[map?.property.name][field.name] = installDocRaw[map?.property.name][field.name] + ', ' + item;
                        }
                      });
                    } else if (append) {
                      installDocRaw[map?.property.name][field.name] = field.default;
                    }
                  } else if (append) {
                    installDocRaw[map?.property.name] = {};
                    installDocRaw[map?.property.name][field.name] = field.default;
                  }
                });
              });
            }
          });
          this.blueprintLatestVersion.installDocRaw = installDocRaw;
          this.getPreviousVersionData(this.blueprintLatestVersion.installDocRaw);
        }

        if (this.getSelectedCapabilities && this.getSelectedCapabilities?.length > 0) {
          if (!this.blueprintLatestVersion?.installDocRaw) {
            this.getSelectedCapabilities.forEach(capability => {
              if (capability.mappingSpecification) {
                const mapping = this.installationSpecificationService.buildDynamicForms(
                  atob(capability.mappingSpecification)
                );
                mapping?.forEach(map => {
                  map?.property?.properties?.forEach((field: any) => {
                    this.installationSpecificationForms?.forEach(form => {
                      if (map?.property?.name === form?.property?.name
                        && form?.property?.properties?.find(item => item?.name === field?.name) && field['x-edit'] === 'append') {
                        if (form.property.properties.find(item => item.name === field.name).default) {
                          const values = form.property.properties.find(item => item.name === field.name).default.split(',').map(item => item.trim());
                          field.default.split(',').map(item => item.trim()).forEach(item => {
                            if (!values.includes(item)) {
                              form.property.properties.find(item => item.name === field.name).default = form.property.properties.find(item => item.name === field.name).default + ', ' + item;
                            }
                          });
                        } else {
                          form.property.properties.find(item => item.name === field.name).default = field.default;
                        }
                      }
                    });
                  });
                });
              }
            });
          }
          this.getSelectedCapabilities.filter((x) => {
            if (x.installParameterMapping) {
              installParameters.push(x.installParameterMapping);

              const decodeInstallParams = Object.assign(JSON.parse(atob(x.installParameterMapping)));
              Object.values(decodeInstallParams).forEach((item) => {
                const splitItems = (item as string).split('.');
                installParamItems.push(splitItems[0].toLowerCase());
              });

              const removeDuplicateItems = [...new Set(installParamItems)];
              uniqueInstallParams = Object.values(removeDuplicateItems);
            }
          });
          installParameters = [...new Set(installParameters)];
          this.installationSpecificationForms = this.installationSpecificationForms.filter((form, index) => {
            if(uniqueInstallParams){
            return uniqueInstallParams.includes(form.property.name.toLowerCase());
          }
          });
          if(installParameters) {
            installParameters.forEach((obj, index) => {
              if (this.installationSpecificationForms[index]?.property) {
                this.installationSpecificationForms[index].property.label = JSON.parse(atob(obj));
              }
            });
          }
        }
        this.installationSpecificationForms.forEach((form, index) => {
          this.stepsArray.push(index);
        });
        this.stepsArray = [...new Set(this.stepsArray)];
      }
    });
  }

  private getPreviousVersionData(installDocRaw): void {
    Object.keys(installDocRaw).forEach(key => {
      const propertyKey = Object.keys(installDocRaw[key]);
      const propertyValue = Object.assign(installDocRaw[key]);
      propertyKey.forEach(controlName => {
        this.installationSpecificationForms.forEach(nestedForm => {
          nestedForm?.formGroup?.controls[key]?.get(controlName)?.setValue(
            propertyValue[controlName].length === undefined ? '' : propertyValue[controlName]);
          nestedForm?.property?.properties?.forEach(element => {
            if (nestedForm?.formGroup?.controls[key]?.get(element.name)) {
              element.updatedValue = nestedForm.formGroup.controls[key].get(element.name).value;
            }
          });
        });
      });
    });
  }

}
